import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

const ContentPageContext = createContext();

export const useContentPage = () => {
  const context = useContext(ContentPageContext);
  if (!context) {
    throw new Error('useContentPage must be used within ContentPageProvider');
  }
  return context;
};

export const ContentPageProvider = ({ children }) => {
  const [sections, setSections] = useState([]);
  const TYPE = 'content-page';

  const createHeroSection = () => ({
    id: 'hero-section',
    type: 'hero',
    isHero: true,
    data: {
      image: '',
      title: '',
      subtitle: ''
    }
  });

  const initializeWithHero = useCallback((existingSections = []) => {
    const hasHero = existingSections.some(section => section.isHero);
    if (!hasHero) {
      return [createHeroSection(), ...existingSections];
    }
    return existingSections;
  }, []);

  const addSection = () => {
    const newSection = {
      id: uuidv4(),
      heading: '',
      subheading: '',
      columns: [{ id: uuidv4(), content: [] }],
    };
    setSections([...sections, newSection]);
  };

  const removeSection = (sectionId) => {
    // Prevent removing hero section
    setSections(sections.filter(section => section.id !== sectionId && !section.isHero));
  };

  const addColumn = (sectionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId && section.columns.length < 3) {
        return {
          ...section,
          columns: [...section.columns, { id: uuidv4(), content: [] }]
        };
      }
      return section;
    }));
  };

  const removeColumn = (sectionId, columnId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          columns: section.columns.filter(col => col.id !== columnId)
        };
      }
      return section;
    }));
  };

  const addContent = (sectionId, columnId, contentType) => {
    const newContent = {
      id: uuidv4(),
      type: contentType,
      data: getDefaultContentData(contentType)
    };

    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          columns: section.columns.map(col => {
            if (col.id === columnId) {
              return {
                ...col,
                content: [...col.content, newContent]
              };
            }
            return col;
          })
        };
      }
      return section;
    }));
  };

  const updateContent = (sectionId, columnId, contentId, data) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          columns: section.columns.map(col => {
            if (col.id === columnId) {
              return {
                ...col,
                content: col.content.map(item => {
                  if (item.id === contentId) {
                    return { ...item, data };
                  }
                  return item;
                })
              };
            }
            return col;
          })
        };
      }
      return section;
    }));
  };

  const removeContent = (sectionId, columnId, contentId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          columns: section.columns.map(col => {
            if (col.id === columnId) {
              return {
                ...col,
                content: col.content.filter(item => item.id !== contentId)
              };
            }
            return col;
          })
        };
      }
      return section;
    }));
  };

  const moveContent = (sectionId, columnId, contentId, direction) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          columns: section.columns.map(col => {
            if (col.id === columnId) {
              const index = col.content.findIndex(item => item.id === contentId);
              if (index === -1) return col;
              
              const newContent = [...col.content];
              const newIndex = direction === 'up' ? index - 1 : index + 1;
              
              if (newIndex >= 0 && newIndex < newContent.length) {
                [newContent[index], newContent[newIndex]] = [newContent[newIndex], newContent[index]];
              }
              
              return { ...col, content: newContent };
            }
            return col;
          })
        };
      }
      return section;
    }));
  };

  const updateHeroSection = (data) => {
    setSections(sections.map(section => {
      if (section.isHero) {
        return {
          ...section,
          data: { ...section.data, ...data }
        };
      }
      return section;
    }));
  };

  const updateSectionHeadings = (sectionId, headingData) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          ...headingData
        };
      }
      return section;
    }));
  };

  const getDefaultContentData = (type) => {
    switch (type) {
      case 'heading':
        return { text: '', level: 'h2' };
      case 'paragraph':
        return { text: '' };
      case 'list':
        return { items: [''], ordered: false };
      case 'image':
        return { url: '', alt: '', caption: '' };
      case 'button':
        return { text: 'Click Here', url: '', style: 'primary' };
      default:
        return {};
    }
  };

  const value = {
    sections,
    setSections,
    TYPE,
    addSection,
    removeSection,
    addColumn,
    removeColumn,
    addContent,
    updateContent,
    removeContent,
    moveContent,
    initializeWithHero,
    updateHeroSection,
    updateSectionHeadings,
  };

  return (
    <ContentPageContext.Provider value={value}>
      {children}
    </ContentPageContext.Provider>
  );
};
